/**
 * 失敗した投稿のカード。
 * - 本文の版が無い（＝生成そのものが失敗）: 再投稿は不可能なので出さず、「作り直す」導線だけ出す（audit C3）
 * - 版がある（＝投稿で失敗）: 本文と媒体ごとのエラーを見せ、「再投稿を試す」は /publish
 * - 一部だけ投稿済み（partially_published）: 残りの媒体の個別承認・結果不明の解消・再投稿。
 *   公開済みの媒体があるので却下はできない（サーバーも 400）
 */

import { Link } from 'react-router-dom'
import type { QueueItem } from '../../lib/postTypes'
import { canRetryPublish, composeRetryHref } from '../../lib/queueRules'
import { InlineError } from '../../components/QueryState'
import { useQueueActions, type Notify } from './useQueueActions'
import { PostBody } from './PostBody'
import { ChannelRows } from './ChannelRows'
import { RejectButton } from './RejectButton'

export function FailedCard({ item, notify }: { item: QueueItem; notify: Notify }) {
  const { post, channels } = item
  const actions = useQueueActions(item, notify)
  const { publish, reject, pending, error } = actions
  const retryable = canRetryPublish(item)
  // 結果不明の媒体があるうちは再投稿させない（二重投稿防止。先に解決してもらう）
  const hasUnresolved = channels.some(c => c.unresolved)
  const partial = post.status === 'partially_published'

  return (
    <div className="gm-card p-4 text-sm">
      <PostBody item={item}
        badge={<span className={`gm-chip shrink-0 ${partial ? 'gm-chip-warn' : 'gm-chip-bad'}`}>
          {partial ? '一部のみ投稿済み' : retryable ? '投稿失敗' : '生成失敗'}
        </span>} />
      {post.errorMessage && <p className="mt-1 text-bad">{post.errorMessage}</p>}
      {retryable
        ? <ChannelRows channels={channels} actions={actions} readOnly={!partial} />
        : (
          <p className="mt-1 text-ink-muted">
            本文の生成に失敗したため、この投稿は再投稿できません。同じお題で作り直してください。
          </p>
        )}
      <div className="mt-3 flex flex-wrap gap-2">
        {retryable ? (
          <button disabled={pending || hasUnresolved} onClick={() => publish.mutate()}
            title={hasUnresolved ? '結果が不明な媒体を先に確認してください' : undefined}
            className="gm-btn gm-btn-secondary">
            再投稿を試す
          </button>
        ) : (
          <Link to={composeRetryHref(post)}
            className="gm-btn gm-btn-secondary">
            作り直す
          </Link>
        )}
        {!partial && (
          <RejectButton label="却下して片付ける" disabled={pending} onConfirm={() => reject.mutate()} className="ml-auto" />
        )}
      </div>
      <InlineError message={error?.message} issues={error?.issues} className="mt-2" />
    </div>
  )
}
